import { abbreviationFromState } from '@contracts/order/valueNormalizers/State';
import { FormAutocomplete } from '@src/common/components/FormAutocomplete';
import { FormDropdown } from '@src/common/components/FormDropdown';
import { HorizontalDivider } from '@src/common/components/HorizontalDivider';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { theme } from '@src/common/styles/Theme';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import { ControllerFormNumberInput } from '@src/orders/PermitFormSections/ControllerFormNumberInput';
import { ControllerFormTextInput } from '@src/orders/PermitFormSections/ControllerFormTextInput';
import { ControllerFormCheckbox } from '@src/orders/PermitFormSections/FormCheckbox';
import { useDisabledFields } from '@src/orders/PermitFormSections/useDisabledFields';
import {
  TractorAxleCountField,
  TractorBodyStyleField,
  TractorFloridaVehicleConfigurationField,
  TractorFuelTypeField,
  TractorIdField,
  TractorLicensePlateNumberField,
  TractorLicensePlateStateField,
  TractorMakeField,
  TractorModelNumberField,
  TractorOntarioVehicleTypeField,
  TractorTypeField,
  TractorUnitNumberField,
  TractorVINField,
  TractorYearField,
  VehicleOfSpecialDesignField,
} from '@src/permitField/TractorInformationFields';
import type { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props {
  control: Control;
  readonly?: boolean;
  fields: string[];
}

export const TractorInformationForm = ({
  control,
  readonly,
  fields,
}: Props) => {
  const { t } = useTranslation();
  const { isFieldDisabled } = useDisabledFields();

  return (
    <Stack direction="column" gap={theme.spacing['3']}>
      <ConditionalField
        fields={fields}
        conditions={TractorUnitNumberField.fieldName}
      >
        <ControllerFormTextInput
          disabled={readonly || isFieldDisabled(TractorIdField.fieldName)}
          control={control}
          fieldName={TractorUnitNumberField.fieldName}
        />
      </ConditionalField>
      <ConditionalField fields={fields} conditions={TractorMakeField.fieldName}>
        <FormAutocomplete
          control={control}
          disabled={isFieldDisabled(TractorMakeField.fieldName)}
          fieldName={TractorMakeField.fieldName}
          options={TractorMakeField.options}
          label={t(`fields.${TractorMakeField.fieldName}`)}
        />
      </ConditionalField>
      <ConditionalField fields={fields} conditions={TractorVINField.fieldName}>
        <ControllerFormTextInput
          uppercase
          control={control}
          fieldName={TractorVINField.fieldName}
          disabled={isFieldDisabled(TractorVINField.fieldName)}
        />
      </ConditionalField>
      <ConditionalField fields={fields} conditions={TractorYearField.fieldName}>
        <ControllerFormNumberInput
          control={control}
          fieldName={TractorYearField.fieldName}
          disabled={isFieldDisabled(TractorYearField.fieldName)}
        />
      </ConditionalField>
      <ConditionalField
        fields={fields}
        conditions={TractorAxleCountField.fieldName}
      >
        <FormDropdown
          control={control}
          fieldName={TractorAxleCountField.fieldName}
          options={TractorAxleCountField.options.map((option) => ({
            value: option,
            label: option,
          }))}
          disabled={isFieldDisabled(TractorAxleCountField.fieldName)}
        />
      </ConditionalField>
      <ConditionalField
        fields={fields}
        conditions={TractorLicensePlateNumberField.fieldName}
      >
        <ControllerFormTextInput
          control={control}
          fieldName={TractorLicensePlateNumberField.fieldName}
          disabled={isFieldDisabled(TractorLicensePlateNumberField.fieldName)}
        />
      </ConditionalField>
      <ConditionalField
        fields={fields}
        conditions={TractorLicensePlateStateField.fieldName}
      >
        <FormDropdown
          control={control}
          fieldName={TractorLicensePlateStateField.fieldName}
          options={TractorLicensePlateStateField.options.map((option) => ({
            label: abbreviationFromState(option),
            value: option,
          }))}
          disabled={isFieldDisabled(TractorLicensePlateStateField.fieldName)}
        />
      </ConditionalField>
      {fields.some((field) =>
        [
          TractorModelNumberField.fieldName,
          TractorModelNumberField.fieldName,
          TractorTypeField.fieldName,
          TractorFuelTypeField.fieldName,
          TractorBodyStyleField.fieldName,
          VehicleOfSpecialDesignField.fieldName,
          TractorFloridaVehicleConfigurationField.fieldName,
          TractorOntarioVehicleTypeField.fieldName,
        ].includes(field),
      ) && (
        <Stack direction="column" gap={theme.spacing['3']}>
          <HorizontalDivider sx={{ margin: '10px' }} textAlign="left">
            <Typography variant="body2">{t('optionalFields')}</Typography>
          </HorizontalDivider>
          <ConditionalField
            fields={fields}
            conditions={TractorModelNumberField.fieldName}
          >
            <ControllerFormTextInput
              control={control}
              fieldName={TractorModelNumberField.fieldName}
              disabled={isFieldDisabled(TractorModelNumberField.fieldName)}
            />
          </ConditionalField>
          <ConditionalField
            fields={fields}
            conditions={TractorTypeField.fieldName}
          >
            <FormDropdown
              control={control}
              fieldName={TractorTypeField.fieldName}
              options={TractorTypeField.options.map((option) => ({
                value: option,
                label: option,
              }))}
              disabled={isFieldDisabled(TractorTypeField.fieldName)}
            />
          </ConditionalField>
          <ConditionalField
            fields={fields}
            conditions={TractorFuelTypeField.fieldName}
          >
            <FormDropdown
              control={control}
              fieldName={TractorFuelTypeField.fieldName}
              options={TractorFuelTypeField.options.map((option) => ({
                value: option,
                label: option,
              }))}
              disabled={isFieldDisabled(TractorFuelTypeField.fieldName)}
            />
          </ConditionalField>
          <ConditionalField
            fields={fields}
            conditions={TractorBodyStyleField.fieldName}
          >
            <FormDropdown
              control={control}
              fieldName={TractorBodyStyleField.fieldName}
              options={TractorBodyStyleField.options.map((option) => ({
                value: option,
                label: option,
              }))}
              disabled={isFieldDisabled(TractorBodyStyleField.fieldName)}
            />
          </ConditionalField>
          <ConditionalField
            fields={fields}
            conditions={TractorFloridaVehicleConfigurationField.fieldName}
          >
            <FormDropdown
              control={control}
              fieldName={TractorFloridaVehicleConfigurationField.fieldName}
              options={TractorFloridaVehicleConfigurationField.options.map(
                (option) => ({
                  value: option,
                  label: t(`fieldOptions.${option}`),
                }),
              )}
              disabled={isFieldDisabled(
                TractorFloridaVehicleConfigurationField.fieldName,
              )}
            />
          </ConditionalField>
          <ConditionalField
            fields={fields}
            conditions={TractorOntarioVehicleTypeField.fieldName}
          >
            <FormDropdown
              control={control}
              fieldName={TractorOntarioVehicleTypeField.fieldName}
              options={TractorOntarioVehicleTypeField.options.map((option) => ({
                value: option,
                label: t(`fieldOptions.${option}`),
              }))}
              disabled={isFieldDisabled(
                TractorOntarioVehicleTypeField.fieldName,
              )}
            />
          </ConditionalField>
          <ConditionalField
            fields={fields}
            conditions={VehicleOfSpecialDesignField.fieldName}
          >
            <ControllerFormCheckbox
              control={control}
              fieldName={VehicleOfSpecialDesignField.fieldName}
              disabled={isFieldDisabled(VehicleOfSpecialDesignField.fieldName)}
            />
          </ConditionalField>
        </Stack>
      )}
    </Stack>
  );
};
